import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/lib/store";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Building2 } from "lucide-react";

export default function Wholesale() {
  const { user, cart, updateQty, addToCart } = useApp();
  const [products, setProducts] = useState([]);
  const [variants, setVariants] = useState({});
  const [form, setForm] = useState({ business_name: "", contact_name: "", email: "", phone: "", business_type: "", tax_id: "", website: "", projected_volume: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/products").then(({ data }) => {
      setProducts(data);
      data.forEach((p) => {
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/variants/by-product/${p.slug}`)
          .then((r) => (r.ok ? r.json() : []))
          .then((v) => setVariants((s) => ({ ...s, [p.slug]: v })))
          .catch(() => {});
      });
    }).catch(() => {});
  }, []);

  const isWholesale = user?.role === "wholesale";

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/wholesale/apply", form);
      toast.success("Application submitted. Under review.");
      setForm({ business_name: "", contact_name: "", email: "", phone: "", business_type: "", tax_id: "", website: "", projected_volume: "", notes: "" });
    } catch (e2) { toast.error("Could not submit application"); }
    setSubmitting(false);
  };

  return (
    <div className="px-4 sm:px-8 lg:px-12 pt-28 pb-20 max-w-7xl mx-auto">
      <div className="mono-label mb-3">B2B</div>
      <h1 className="font-serif text-5xl sm:text-6xl text-white mb-4">CG Labz Professional<br />Research Accounts</h1>
      <p className="text-slate-400 text-sm max-w-xl mb-12">Wholesale pricing, quick-order matrix, lot-aware inventory, and dedicated account management for laboratories and resellers.</p>

      {!isWholesale ? (
        <div className="grid lg:grid-cols-2 gap-10">
          <div className="glass rounded-3xl p-8">
            <div className="mono-label mb-4">Apply for access</div>
            <form onSubmit={submit} className="space-y-4">
              {[["business_name","Business name *"],["contact_name","Contact name *"],["email","Email *"],["phone","Phone"],["business_type","Business type"],["tax_id","Tax ID / resale doc"],["website","Website"],["projected_volume","Projected monthly volume"]].map(([k, ph]) => (
                <Input key={k} required={ph.includes("*")} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} placeholder={ph}
                  data-testid={`wholesale-input-${k}`} className="bg-black/40 border-white/10" />
              ))}
              <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notes"
                data-testid="wholesale-input-notes" className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm min-h-[80px]" />
              <Button type="submit" disabled={submitting} data-testid="wholesale-apply-btn" className="w-full btn-primary rounded-xl py-3 text-sm">
                {submitting ? "Submitting…" : "Submit Application"}
              </Button>
            </form>
          </div>
          <div className="space-y-4">
            {[["Tiered pricing","WHOLESALE 1 / 2 / DISTRIBUTOR / STRATEGIC ACCOUNT tiers with custom terms."],["Quick Order","Spreadsheet-style matrix to add 50+ SKUs in seconds."],["Lot-aware inventory","Every vial traceable to a verified batch and COA."],["Quote builder","Request custom quotes for large-volume orders."]].map(([t, d]) => (
              <div key={t} className="glass rounded-2xl p-5 flex gap-4">
                <Building2 className="text-cyan-300 shrink-0" size={18} />
                <div><div className="text-sm font-medium text-white">{t}</div><div className="text-xs text-slate-400 mt-1">{d}</div></div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div>
          <div className="glass rounded-2xl p-4 mb-6 flex flex-wrap items-center gap-3">
            <span className="stat-pill">{user.wholesale_tier || "WHOLESALE_1"}</span>
            <span className="text-xs font-mono text-slate-400">Wholesale pricing active · minimum order rules apply</span>
          </div>
          <div className="glass rounded-2xl overflow-hidden" data-testid="wholesale-quick-order-table">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10">
                    {["SKU","Peptide","Strength","Stock","Wholesale","Retail","Qty",""].map((h) => (
                      <th key={h} className="p-3 text-left text-[10px] font-mono uppercase tracking-widest text-slate-500">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {products.flatMap((p) => (variants[p.slug] || []).map((v) => (
                    <tr key={v.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`quick-order-sku-row-${v.sku}`}>
                      <td className="p-3 font-mono text-xs text-slate-400">{v.sku}</td>
                      <td className="p-3 text-white">{p.name.replace("CG Labz ", "")}</td>
                      <td className="p-3 font-mono text-xs">{v.mg}{v.unit}</td>
                      <td className="p-3 font-mono text-xs text-slate-400">{v.inventory}</td>
                      <td className="p-3 font-mono text-cyan-300">${v.wholesale_price?.toFixed(2)}</td>
                      <td className="p-3 font-mono text-xs text-slate-500 line-through">${v.price?.toFixed(2)}</td>
                      <td className="p-3">
                        <input type="number" min="0" defaultValue="0" id={`qo-${v.id}`}
                          className="w-16 bg-black/40 border border-white/10 rounded-lg px-2 py-1 text-sm font-mono" />
                      </td>
                      <td className="p-3">
                        <Button size="sm" onClick={() => {
                          const q = parseInt(document.getElementById(`qo-${v.id}`).value) || 0;
                          if (q < 1) return toast.error("Enter quantity");
                          addToCart({ ...v, price: v.wholesale_price }, p, q);
                          toast.success(`${q} × ${v.sku} added`);
                        }} data-testid={`quick-order-add-${v.sku}`} className="btn-primary rounded-lg text-xs">Add</Button>
                      </td>
                    </tr>
                  )))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
